/**
 * Navigation section definitions for Quick Navigation FAB
 * Organized by tab/page
 */

import type { NavigationSection } from '../components/QuickNavigationFAB';

export const CALCULATOR_SECTIONS: NavigationSection[] = [
  {
    id: 'birth-details',
    label: 'Birth Details',
    scrollToId: 'section-birth-details',
  },
  {
    id: 'chart',
    label: 'Smart Chart',
    scrollToId: 'section-chart',
  },
  {
    id: 'house-scores',
    label: 'House Analysis',
    scrollToId: 'section-house-analysis-default',
  },
  {
    id: 'life-areas',
    label: 'Life Areas Dashboard',
    scrollToId: 'section-life-areas-default',
  },
  {
    id: 'timeline',
    label: 'Dasha Timeline',
    scrollToId: 'section-dasha-timeline-default',
  },
  {
    id: 'basic-details',
    label: 'Basic Details',
    scrollToId: 'section-basic-details',
  },
  {
    id: 'planet-details',
    label: 'Planet Details',
    scrollToId: 'section-planet-details',
  },
  {
    id: 'yogas',
    label: 'Yogas Analysis',
    scrollToId: 'section-yogas',
  },
  {
    id: 'five-fold-friends',
    label: 'Five Fold Friendship',
    scrollToId: 'section-five-fold-friends',
  },
];

// Mobile-specific sections for Calculator tab
export const CALCULATOR_SECTIONS_MOBILE: NavigationSection[] = [
  {
    id: 'birth-details',
    label: 'Birth Details',
    scrollToId: 'section-birth-details',
  },
  {
    id: 'chart',
    label: 'Smart Chart',
    scrollToId: 'section-advanced-chart-mobile',
  },
  {
    id: 'house-scores',
    label: 'House Analysis',
    scrollToId: 'section-house-analysis-default',
  },
  {
    id: 'life-areas',
    label: 'Life Areas Dashboard',
    scrollToId: 'section-life-areas-default',
  },
  {
    id: 'timeline',
    label: 'Dasha Timeline',
    scrollToId: 'section-dasha-timeline-mobile',
  },
  {
    id: 'basic-details',
    label: 'Basic Details',
    scrollToId: 'section-basic-details',
  },
  {
    id: 'planet-details',
    label: 'Planet Details',
    scrollToId: 'section-planet-details',
  },
  {
    id: 'yogas',
    label: 'Yogas Analysis',
    scrollToId: 'section-yogas',
  },
  {
    id: 'five-fold-friends',
    label: 'Five Fold Friendship',
    scrollToId: 'section-five-fold-friends',
  },
];

export const DASHA_SECTIONS: NavigationSection[] = [
  {
    id: 'timeline-view',
    label: 'Dasha Timeline View',
    scrollToId: 'section-dasha-timeline',
  },
  {
    id: 'active-yogas',
    label: 'Active Yogas & Predictions',
    scrollToId: 'section-active-yogas',
  },
  {
    id: 'life-areas-analysis',
    label: 'Current Life Area Analysis',
    scrollToId: 'section-life-areas-analysis',
  },
  {
    id: 'future-events',
    label: 'Future Events Predictor',
    scrollToId: 'section-future-events',
  },
];

export const FEEDS_SECTIONS: NavigationSection[] = [
  {
    id: 'personalized-feeds',
    label: 'Personalized Astrology Feeds',
    scrollToId: 'section-personalized-feeds',
  },
  {
    id: 'permanent-yogas',
    label: 'Permanent Yogas & Doshas',
    onClick: () => {
      // Click Tab 0 (Permanent Features & Yogas)
      const tab0 = document.querySelector('[role="tab"][value="0"]') as HTMLElement;
      if (tab0) tab0.click();
      // Scroll after brief delay to allow tab content to render
      setTimeout(() => {
        const element = document.getElementById('section-permanent-yogas');
        if (element) {
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 150);
    },
  },
  {
    id: 'doshas',
    label: 'Doshas Detected',
    onClick: () => {
      // Click Tab 2 (Doshas and Planetary Combinations)
      const tab2 = document.querySelector('[role="tab"][value="2"]') as HTMLElement;
      if (tab2) tab2.click();
      // Scroll after brief delay to allow tab content to render
      setTimeout(() => {
        const element = document.getElementById('section-doshas');
        if (element) {
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 150);
    },
  },
  {
    id: 'planetary-combinations',
    label: 'Planetary Combinations',
    onClick: () => {
      // Click Tab 2 (Doshas and Planetary Combinations)
      const tab2 = document.querySelector('[role="tab"][value="2"]') as HTMLElement;
      if (tab2) tab2.click();
      // Scroll after brief delay to allow tab content to render
      setTimeout(() => {
        const element = document.getElementById('section-planetary-combinations');
        if (element) {
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 150);
    },
  },
];

// Mobile-specific sections for Feeds tab (simplified for mobile view without tabs)
export const FEEDS_SECTIONS_MOBILE: NavigationSection[] = [
  {
    id: 'personalized-feeds',
    label: 'Personalized Astrology Feeds',
    scrollToId: 'section-personalized-feeds',
  },
  {
    id: 'permanent-yogas',
    label: 'Permanent Yogas & Doshas',
    onClick: () => {
      // Scroll to top first to ensure visibility
      window.scrollTo({ top: 0, behavior: 'auto' });
      setTimeout(() => {
        // Click Tab 0 (Permanent Features & Yogas) - use text-based selector for mobile
        const tabs = document.querySelectorAll('[role="tab"]');
        const tab0 = Array.from(tabs).find(tab => tab.textContent?.includes('Permanent')) as HTMLElement;
        if (tab0) {
          tab0.click();
          // Scroll after brief delay to allow tab content to render
          setTimeout(() => {
            const element = document.getElementById('section-permanent-yogas');
            if (element) {
              const yOffset = -100;
              const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y, behavior: 'smooth' });
            }
          }, 200);
        }
      }, 100);
    },
  },
  {
    id: 'doshas',
    label: 'Doshas Detected',
    onClick: () => {
      // Scroll to top first to ensure visibility
      window.scrollTo({ top: 0, behavior: 'auto' });
      setTimeout(() => {
        // Click Tab 2 (Doshas and Planetary Combinations) - use text-based selector for mobile
        const tabs = document.querySelectorAll('[role="tab"]');
        const tab2 = Array.from(tabs).find(tab => tab.textContent?.includes('Doshas')) as HTMLElement;
        if (tab2) {
          tab2.click();
          // Scroll after brief delay to allow tab content to render
          setTimeout(() => {
            const element = document.getElementById('section-doshas');
            if (element) {
              const yOffset = -100;
              const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y, behavior: 'smooth' });
            }
          }, 200);
        }
      }, 100);
    },
  },
  {
    id: 'planetary-combinations',
    label: 'Planetary Combinations',
    onClick: () => {
      // Scroll to top first to ensure visibility
      window.scrollTo({ top: 0, behavior: 'auto' });
      setTimeout(() => {
        // Click Tab 2 (Doshas and Planetary Combinations) - use text-based selector for mobile
        const tabs = document.querySelectorAll('[role="tab"]');
        const tab2 = Array.from(tabs).find(tab => tab.textContent?.includes('Doshas')) as HTMLElement;
        if (tab2) {
          tab2.click();
          // Scroll after brief delay to allow tab content to render
          setTimeout(() => {
            const element = document.getElementById('section-planetary-combinations');
            if (element) {
              const yOffset = -100;
              const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({ top: y, behavior: 'smooth' });
            }
          }, 200);
        }
      }, 100);
    },
  },
];

export const MYCHART_SECTIONS: NavigationSection[] = [
  {
    id: 'charts-d1-d9',
    label: 'D1 and D9 Chart',
    scrollToId: 'section-charts-d1-d9',
  },
  {
    id: 'planet-factors',
    label: 'Planet Factors',
    scrollToId: 'section-planet-factors',
  },
  {
    id: 'd1-house-scores',
    label: 'D1 House Scores',
    scrollToId: 'section-d1-house-scores',
  },
  {
    id: 'life-areas-remedies',
    label: 'Life Areas Summary with Remedies',
    scrollToId: 'section-life-areas-remedies',
  },
  {
    id: 'house-collation',
    label: 'D1 House Collation',
    scrollToId: 'section-house-collation',
  },
  {
    id: 'house-predictions',
    label: 'House wise predictions',
    scrollToId: 'section-house-predictions',
  },
];
